import { DATE_DISPLAY_PATTERNS } from '@config/dateFormatConfig';
import { emitLocalizationWarning } from '@utils/localizationWarnings';

// ---------------------------------------------------------------------------
// Public types — see specs/105-ui-localization-config/contracts/localization-config.contract.md
// ---------------------------------------------------------------------------

export type SupportedLocale = 'en-US' | 'fr-CA';

export type SupportedDatePattern = 'MM/DD/YYYY' | 'DD/MM/YYYY' | 'MMM Do, YYYY' | 'MMM DD, YYYY';

export type WarningCode =
  | 'LOCALIZATION_CONFIG_FIELD_MISSING'
  | 'LOCALIZATION_UNSUPPORTED_LANGUAGE'
  | 'LOCALIZATION_INVALID_DATE_PATTERN'
  | 'LOCALIZATION_OVERRIDE_BLOCKED_LANGUAGE_CHANGE';

export interface LocalizationConfig {
  readonly language: SupportedLocale;
  readonly languageOverrideEnabled: boolean;
  readonly dateFormatPattern: SupportedDatePattern;
}

// ---------------------------------------------------------------------------
// Whitelists
// ---------------------------------------------------------------------------

export const SUPPORTED_LOCALES: readonly SupportedLocale[] = ['en-US', 'fr-CA'] as const;

export const SUPPORTED_DATE_PATTERNS: readonly SupportedDatePattern[] = [
  'MM/DD/YYYY',
  'DD/MM/YYYY',
  'MMM Do, YYYY',
  'MMM DD, YYYY',
] as const;

export const WARNING_CODES = {
  CONFIG_FIELD_MISSING: 'LOCALIZATION_CONFIG_FIELD_MISSING',
  UNSUPPORTED_LANGUAGE: 'LOCALIZATION_UNSUPPORTED_LANGUAGE',
  INVALID_DATE_PATTERN: 'LOCALIZATION_INVALID_DATE_PATTERN',
  OVERRIDE_BLOCKED_LANGUAGE_CHANGE: 'LOCALIZATION_OVERRIDE_BLOCKED_LANGUAGE_CHANGE',
} as const satisfies Record<string, WarningCode>;

// ---------------------------------------------------------------------------
// Defaults — derived from existing internal helpers per data-model.md §5
// ---------------------------------------------------------------------------

export const LOCALIZATION_DEFAULTS: Readonly<LocalizationConfig> = Object.freeze({
  language: 'en-US' as SupportedLocale,
  languageOverrideEnabled: true,
  dateFormatPattern: DATE_DISPLAY_PATTERNS.monthNameNumeric as SupportedDatePattern,
});

// ---------------------------------------------------------------------------
// Source-of-truth literal (today). Future SC-006 work may replace this with a
// cached fetch result without touching the public surface.
// ---------------------------------------------------------------------------

const localizationConfig = Object.freeze({
  language: 'en-US',
  languageOverrideEnabled: false,
  dateFormatPattern: 'MMM DD, YYYY',
}) as Readonly<Record<keyof LocalizationConfig, unknown>>;

// ---------------------------------------------------------------------------
// Accessor
// ---------------------------------------------------------------------------

function isSupportedLocale(value: unknown): value is SupportedLocale {
  return typeof value === 'string' && (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

function isSupportedDatePattern(value: unknown): value is SupportedDatePattern {
  return (
    typeof value === 'string' && (SUPPORTED_DATE_PATTERNS as readonly string[]).includes(value)
  );
}

interface BuildOptions {
  readonly source?: Readonly<Record<string, unknown>>;
}

function buildConfig(source: Readonly<Record<string, unknown>>): Readonly<LocalizationConfig> {
  // language
  let language: SupportedLocale;
  const rawLanguage = source.language;
  if (typeof rawLanguage !== 'string') {
    emitLocalizationWarning({
      code: 'LOCALIZATION_CONFIG_FIELD_MISSING',
      field: 'language',
      defaultApplied: LOCALIZATION_DEFAULTS.language,
    });
    language = LOCALIZATION_DEFAULTS.language;
  } else if (!isSupportedLocale(rawLanguage)) {
    emitLocalizationWarning({
      code: 'LOCALIZATION_UNSUPPORTED_LANGUAGE',
      configured: rawLanguage,
      fallback: LOCALIZATION_DEFAULTS.language,
    });
    language = LOCALIZATION_DEFAULTS.language;
  } else {
    language = rawLanguage;
  }

  // languageOverrideEnabled
  let languageOverrideEnabled: boolean;
  const rawOverride = source.languageOverrideEnabled;
  if (typeof rawOverride !== 'boolean') {
    emitLocalizationWarning({
      code: 'LOCALIZATION_CONFIG_FIELD_MISSING',
      field: 'languageOverrideEnabled',
      defaultApplied: LOCALIZATION_DEFAULTS.languageOverrideEnabled,
    });
    languageOverrideEnabled = LOCALIZATION_DEFAULTS.languageOverrideEnabled;
  } else {
    languageOverrideEnabled = rawOverride;
  }

  // dateFormatPattern
  let dateFormatPattern: SupportedDatePattern;
  const rawPattern = source.dateFormatPattern;
  if (typeof rawPattern !== 'string') {
    emitLocalizationWarning({
      code: 'LOCALIZATION_CONFIG_FIELD_MISSING',
      field: 'dateFormatPattern',
      defaultApplied: LOCALIZATION_DEFAULTS.dateFormatPattern,
    });
    dateFormatPattern = LOCALIZATION_DEFAULTS.dateFormatPattern;
  } else if (!isSupportedDatePattern(rawPattern)) {
    emitLocalizationWarning({
      code: 'LOCALIZATION_INVALID_DATE_PATTERN',
      configured: rawPattern,
      fallback: LOCALIZATION_DEFAULTS.dateFormatPattern,
    });
    dateFormatPattern = LOCALIZATION_DEFAULTS.dateFormatPattern;
  } else {
    dateFormatPattern = rawPattern;
  }

  return Object.freeze({ language, languageOverrideEnabled, dateFormatPattern });
}

/**
 * Returns the active localization configuration with every field validated and
 * defaults substituted for any missing or malformed value. Always returns a
 * frozen object. Never throws.
 *
 * The optional `options.source` parameter is a test-only seam — production
 * callers MUST invoke `getLocalizationConfig()` with no arguments, which reads
 * the module-level source-of-truth literal.
 */
export function getLocalizationConfig(options?: BuildOptions): Readonly<LocalizationConfig> {
  const source = options?.source ?? localizationConfig;
  return buildConfig(source);
}
